import {
  BMComponentsType,
  BMPrimitives,
  BMSkeletonLoader,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemChevronDown from "@traveloka/icon-kit-web/react/IcSystemChevronDown";
import { memo, useMemo } from "react";
import { Animated, Pressable, StyleSheet } from "react-native";
import BMView from "../../../components/View/BMView";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import useCollapsibleAnimation from "../../hooks/hooks";

type Props = {
  total: string | undefined;
  breakdowns: Array<{ label: string; value: string }> | undefined;
};

/**
 * @todo
 * Create this as a generic component to be used in all products (not only flight)
 * change the name to ProductPriceBreakdown
 *
 * @component
 * Renders a price breakdown section with an accordion to show/hide detailed breakdowns.
 *
 * @param {Array<{ label: string; value: string }>} breakdowns - Array of price breakdown items, each with a label and value.
 * @param {string} total - Total price to be displayed.
 */
function ProductPriceBreakdown({ breakdowns, total }: Props) {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);
  const { CorporateProductPriceBreakdown: cr } = useMobileContentResource();

  const {
    contentAnimatedStyle,
    contentWrapperAnimatedStyle,
    chevronAnimatedStyle,
    onLayoutContentWrapper,
    onToggleAnimate,
  } = useCollapsibleAnimation({
    isAccordion: true,
    isDefaultOpen: false,
  });

  return (
    <BMView style={styles.card}>
      <Pressable onPress={onToggleAnimate}>
        <BMView row justify="between" align="center">
          <BMText variant="uiLarge" ink="BMStyleTextInkSecondary">
            {cr.total}
          </BMText>
          <BMView row spacing="md" align="center">
            {total ? (
              <BMText variant="uiLarge">{total}</BMText>
            ) : (
              <BMSkeletonLoader width={120} height={20} />
            )}
            <Animated.View style={chevronAnimatedStyle}>
              <IcSystemChevronDown color={BMComponents.BMStyleIconInkAction} />
            </Animated.View>
          </BMView>
        </BMView>
      </Pressable>
      {breakdowns?.length ? (
        <Animated.View style={contentWrapperAnimatedStyle}>
          <Animated.View
            onLayout={onLayoutContentWrapper}
            style={contentAnimatedStyle}
          >
            <BMView spacing="md" style={styles.marginTopMd}>
              {breakdowns.map((price, index) => (
                <BMView key={index} row justify="between">
                  <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
                    {price.label}
                  </BMText>
                  <BMText variant="uiSmallLi" ink="BMStyleTextInkPrimary">
                    {price.value}
                  </BMText>
                </BMView>
              ))}
            </BMView>
          </Animated.View>
        </Animated.View>
      ) : null}
    </BMView>
  );
}

export default memo(ProductPriceBreakdown);

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    card: {
      padding: BMPrimitives.BMSize.md,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderRadius: BMPrimitives.BMRadius.md,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      overflow: "hidden",
    },
    marginTopMd: {
      marginTop: BMPrimitives.BMSize.md,
    },
  });
}
